import { FC } from 'react';

import {
  ChannelBalanceSummary,
  ChannelReconcileResponse,
  GiftChannelLedgerType,
} from '@shared/api/giftChannelLedger/giftChannelLedger.interface';
import {
  DELIVERY_CHANNEL_CONFIG,
  DeliveryChannel,
} from '@shared/api/giftCoupon/giftCoupon.interface';

import { kstShortDateTimeLabel, kstToday } from '../../utils/kstTime.utils';

interface Props {
  balances: ChannelBalanceSummary[];
  reconcileInputs: Partial<Record<DeliveryChannel, string>>;
  setReconcileInput: (channel: DeliveryChannel, value: string) => void;
  reconcileResults: Partial<Record<DeliveryChannel, ChannelReconcileResponse>>;
  reconcilingChannel: DeliveryChannel | null;
  onReconcile: (channel: DeliveryChannel) => void;
  onOpenManual: (
    type: GiftChannelLedgerType,
    channel: DeliveryChannel,
    memoPreset?: string,
  ) => void;
}

const formatOccurredAt = (date: Date | null) =>
  date ? kstShortDateTimeLabel(new Date(date)) : null;

/** 채널별 잔액 카드 + 실잔액 대사 — 발송 처리 직후 대조가 핵심 흐름 */
export const ChannelBalanceCards: FC<Props> = ({
  balances,
  reconcileInputs,
  setReconcileInput,
  reconcileResults,
  reconcilingChannel,
  onReconcile,
  onOpenManual,
}) => {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {balances.map((summary) => {
        const config = DELIVERY_CHANNEL_CONFIG[summary.channel];
        const result = reconcileResults[summary.channel];
        // 대사 결과는 조회 시점 잔액 기준 — 이후 기장이 있었으면 훅이 결과를 비운다
        const isEmpty = summary.entryCount === 0;
        return (
          <div
            key={summary.channel}
            className={`rounded-lg border bg-white p-4 shadow-sm ${
              summary.isNegative ? 'border-red-300' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <span
                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${config.badgeClass}`}
              >
                {config.label}
              </span>
              {summary.isNegative && (
                <span className="text-[11px] font-bold text-red-600">잔액 음수</span>
              )}
            </div>

            <p
              className={`mt-2 text-xl font-bold ${
                summary.isNegative ? 'text-red-600' : 'text-gray-900'
              }`}
            >
              {summary.balance.toLocaleString()}원
            </p>
            <p className="mt-0.5 text-[11px] text-gray-400">
              {isEmpty
                ? '기록 없음'
                : `기록 ${summary.entryCount.toLocaleString()}건 · 마지막 ${
                    formatOccurredAt(summary.lastOccurredAt) ?? '—'
                  }`}
            </p>
            {summary.isNegative && (
              <p className="mt-1 text-[11px] text-red-500">
                입금 미기록 또는 도매가 오입력 신호 — 발송은 차단되지 않습니다.
              </p>
            )}

            {isEmpty ? (
              <button
                type="button"
                onClick={() =>
                  onOpenManual(
                    GiftChannelLedgerType.Adjust,
                    summary.channel,
                    `기초잔액 (${kstToday()} 실사이트 잔액)`,
                  )
                }
                className="mt-3 w-full rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
              >
                기초잔액 입력 (실사이트 잔액)
              </button>
            ) : (
              <div className="mt-3 border-t pt-2">
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={reconcileInputs[summary.channel] ?? ''}
                    onChange={(e) => setReconcileInput(summary.channel, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onReconcile(summary.channel);
                    }}
                    placeholder="실제 사이트 잔액(원)"
                    className="w-full min-w-0 rounded border border-gray-300 px-2 py-1 text-xs focus:border-indigo-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => onReconcile(summary.channel)}
                    disabled={reconcilingChannel === summary.channel}
                    className="shrink-0 rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {reconcilingChannel === summary.channel ? '대사중…' : '대사'}
                  </button>
                </div>

                {result &&
                  (result.matched ? (
                    <p className="mt-1.5 text-xs font-medium text-emerald-600">
                      ✓ 일치 — {result.ourBalance.toLocaleString()}원
                    </p>
                  ) : (
                    <div className="mt-1.5">
                      <p className="text-xs font-bold text-red-600">
                        차액 {result.diff > 0 ? '+' : ''}
                        {result.diff.toLocaleString()}원 (실잔액{' '}
                        {result.actualBalance.toLocaleString()} − 미러{' '}
                        {result.ourBalance.toLocaleString()})
                      </p>
                      <p className="mt-0.5 text-[11px] leading-4 text-gray-500">
                        ① 도매가 오입력(발송내역에서 교정 → 자동 재기장) ② 입금 미기록 ③
                        무관사용 미기록 순으로 확인하세요.
                      </p>
                    </div>
                  ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
